import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "h-full" }
const _hoisted_2 = { class: "navbar bg-[#0b295b] flex justify-between" }
const _hoisted_3 = { class: "flex" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("a", { class: "btn btn-ghost text-xl text-neutral-content" }, "CONROO", -1)
const _hoisted_5 = { class: "text-neutral-content" }
const _hoisted_6 = { class: "text-neutral-content" }
const _hoisted_7 = { class: "flex pt-12 justify-center flex-col h-full" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("div", { class: "text-4xl md:text-6xl ml-12 text-[#0b295b] font-bold" }, [
  /*#__PURE__*/_createElementVNode("h1", null, "User Management")
], -1)
const _hoisted_9 = {
  key: 0,
  class: "mt-4"
}
const _hoisted_10 = { key: 1 }
const _hoisted_11 = /*#__PURE__*/_createElementVNode("div", { class: "flex justify-center mt-24 pt-24" }, [
  /*#__PURE__*/_createElementVNode("span", { class: "loading loading-spinner loading-lg" })
], -1)
const _hoisted_12 = [
  _hoisted_11
]
const _hoisted_13 = { key: 2 }
const _hoisted_14 = { class: "font-bold text-white opacity-100" }
const _hoisted_15 = {
  key: 3,
  class: "mt-8 overflow-x-scroll h-full"
}
const _hoisted_16 = {
  key: 0,
  class: "pl-8 pr-8"
}
const _hoisted_17 = { key: 4 }
const _hoisted_18 = /*#__PURE__*/_createElementVNode("span", { class: "loading loading-bars loading-lg" }, null, -1)
const _hoisted_19 = [
  _hoisted_18
]

import ManagedUserTable from "./ManagedUserTable.vue";
import { reactive, onMounted, ref, onUnmounted } from "vue";
import UserSearchBar from "./UserSearchBar.vue";
import { useUserStore } from "@/observables/store/user-store";
import { getAuthService } from "@/services/auth/auth-service";
import { useRouter } from "vue-router";
import { getAdminClient } from "@/services/api-client";
import {
  IspsTruckerChangeTO,
  IspsTruckerChangeTODocumentStatusEnum,
  IspsTruckerChangeTOTerrorStatusEnum,
  IspsTruckerTO,
  TruckerIdentificationTODocumentStatusEnum,
  TruckerIdentificationTOTerrorStatusEnum,
} from "@/services/api-client/client";
import { usePagination } from "@/observables/pagination";
import { isAdmin } from "@/util/role-util";

interface Toast {
  message: string;
  toastType: "alert-success" | "alert-error" | "alert-warn" | "alert-info";
}

const POLLING_TIME_MILLISECONDS = 1000 * 180; // 3 minutes


export default /*#__PURE__*/_defineComponent({
  __name: 'UsersManagement',
  setup(__props) {

const userStore = useUserStore();
const authService = getAuthService();
const adminClient = getAdminClient();
const admin = userStore.getUser();
const adminId = admin?.id;
const toast = ref<Toast>({ message: "", toastType: "alert-info" });

const fetchTruckers = async (pageSize: number, pageIndex: number) => {
  if (adminId == null) {
    return false;
  }
  const currentPageUsers = await adminClient.getIspsTruckers(
    adminId,
    searchPayload.warningState,
    searchPayload.terrorState,
    searchPayload.userId,
    pageIndex,
    pageSize
  );

  users.data.push(...currentPageUsers);
  if (currentPageUsers.length < pageSize) {
    return true;
  }

  return false;
};

const { pageSize, pageIndex, loading, restartPageIndex, incrementPageIndex } =
  usePagination(fetchTruckers);
const router = useRouter();

const filledData = ref<boolean>(false);
let intervalId: number | null = null;
let searchPayload: {
  userId: string | undefined;
  warningState: IspsTruckerChangeTODocumentStatusEnum | undefined;
  terrorState: IspsTruckerChangeTOTerrorStatusEnum | undefined;
} = reactive({
  userId: undefined,
  warningState: undefined,
  terrorState: undefined,
});

onMounted(async () => {
  await fetchTruckers(pageSize, pageIndex.value);
  incrementPageIndex();
  filledData.value = true;

  intervalId = setInterval(async () => {
    await filterUsers(searchPayload);
  }, POLLING_TIME_MILLISECONDS);
});

onUnmounted(() => {
  if (intervalId != null) {
    clearInterval(intervalId);
  }
});
const users = reactive<{ data: IspsTruckerTO[] }>({
  data: [],
});

const filterUsers = async (payload: {
  userId: string | undefined;
  terrorState: TruckerIdentificationTOTerrorStatusEnum | undefined;
  warningState: TruckerIdentificationTODocumentStatusEnum | undefined;
}) => {
  if (adminId == null) {
    return;
  }
  const { userId, terrorState, warningState } = payload;
  searchPayload = payload;
  //use pagination is not a good abstraction atm, share state in a readonly result, and provide an interface for functions to interact in a generic way
  restartPageIndex();
  const searchedUsers = await adminClient.getIspsTruckers(
    adminId,
    warningState,
    terrorState,
    userId?.trim(),
    pageIndex.value,
    pageSize
  );

  incrementPageIndex();
  users.data = searchedUsers;
};

const addUserRevision = async (payload: {
  userId: string;
  data: IspsTruckerChangeTO;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  await adminClient.ispsTruckerLog(adminId!, payload.userId, payload.data);
  //absolutely must be fixed, just a quick dirty code
  filterUsers({
    userId: undefined,
    terrorState: undefined,
    warningState: undefined,
  });
};

const showToast = (
  message: string,
  alertType: "success" | "error" | "warn" | "info"
) => {
  toast.value.message = message;
  toast.value.toastType = `alert-${alertType}`;
  setTimeout(() => {
    toast.value.message = "";
    toast.value.toastType = "alert-info";
  }, 3000);
};

const resetIDNow = async (payload: { userId: string }) => {
  if (adminId == null) {
    return;
  }
  try {
    await adminClient.deleteTruckerIdentifications(adminId, payload.userId);
    showToast("IDnow verification attempts succesfully resetted", "success");
  } catch (e) {
    showToast("Error reseting IDnow tries for user", "error");
    console.error(e);
  }
};

//TODO: this should be for all pages, this is just simple enough for now.
const logout = async () => {
  await authService.logout();
  userStore.removeUser();
  router.push("/");
  //this sucks so much haha hehe haha
};

const approveUser = async (payload: {
  userId: string;
  entryId: number;
  approve: boolean;
}) => {
  const { userId, entryId, approve } = payload;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  //TODO: ISSUE WITH 204 - 200 fix in backend
  try {
    if (adminId == null) {
      return;
    }
    await adminClient.patchIspsTrucker(adminId, userId, entryId, {
      approved: approve,
    });
  } catch (e) {
    console.log(e);
  }

  filterUsers({
    userId: undefined,
    terrorState: undefined,
    warningState: undefined,
  });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", null, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _hoisted_4,
          _createElementVNode("div", _hoisted_5, _toDisplayString(`Current ${_unref(isAdmin)(_unref(admin)?.roles ?? []) ? "Admin" : "User"}: ${
                _unref(admin)?.firstName
              } ${_unref(admin)?.lastName}`), 1)
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("button", {
            onClick: _cache[0] || (_cache[0] = () => logout()),
            class: "btn btn-error btn-sm mr-2"
          }, " Log out ")
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_7, [
      _hoisted_8,
      (filledData.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createVNode(UserSearchBar, {
              onSubmit: _cache[1] || (_cache[1] = (t) => filterUsers(t))
            })
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_10, _hoisted_12)),
      (toast.value.message)
        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
            _createElementVNode("div", {
              role: "alert",
              class: _normalizeClass(`alert ${toast.value.toastType}`)
            }, [
              _createElementVNode("span", _hoisted_14, _toDisplayString(toast.value.message), 1)
            ], 2)
          ]))
        : _createCommentVNode("", true),
      (users.data.length !== 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
            (users.data.length !== 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                  _createVNode(ManagedUserTable, {
                    onApprove: _cache[2] || (_cache[2] = (p) => approveUser(p)),
                    onSubmit: _cache[3] || (_cache[3] = (p) => addUserRevision(p)),
                    onReset: _cache[4] || (_cache[4] = (p) => resetIDNow(p)),
                    users: users.data
                  }, null, 8, ["users"])
                ]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      (users.data.length !== 0 && _unref(loading))
        ? (_openBlock(), _createElementBlock("div", _hoisted_17, _hoisted_19))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})